import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Bot, CloudCog, Lock, Shield, Database, Radar } from "lucide-react";

const NODES = [
  { id: "cloud", label: "Cloud", icon: CloudCog, x: 50, y: 15 },
  { id: "ia", label: "IA", icon: Bot, x: 84, y: 32 },
  { id: "soc", label: "SOC", icon: Radar, x: 84, y: 68 },
  { id: "securite", label: "Sécurité", icon: Shield, x: 50, y: 85 },
  { id: "donnees", label: "Données", icon: Database, x: 16, y: 68 },
  { id: "zero-trust", label: "Zero Trust", icon: Lock, x: 16, y: 32 },
];

const CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0],
  [0, 2], [0, 3], [0, 4], [1, 3], [1, 5], [2, 4], [3, 5], [4, 1],
];

function Particles({ width, height }) {
  const canvasRef = useRef(null);
  const particles = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const count = 40;

    canvas.width = width;
    canvas.height = height;

    if (particles.current.length === 0) {
      particles.current = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
      }));
    }

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const pts = particles.current;
      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(161,95,228,0.25)";
        ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 100) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(161,95,228,${0.08 * (1 - d / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [width, height]);

  return <canvas ref={canvasRef} className="absolute inset-0" style={{ width, height }} />;
}

export function HeroVisual() {
  const w = 520;
  const h = 480;

  return (
    <div
      className="relative mx-auto aspect-square w-full max-w-[520px]"
      data-testid="home-hero-visual"
    >
      <div className="absolute inset-0 rounded-[2.5rem] border border-border/40 bg-gradient-to-br from-card/80 to-background/60 backdrop-blur-sm overflow-hidden">
        <Particles width={w} height={h} />

        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {CONNECTIONS.map(([a, b], i) => (
            <motion.line
              key={`line-${NODES[a].id}-${NODES[b].id}`}
              x1={`${NODES[a].x}%`} y1={`${NODES[a].y}%`}
              x2={`${NODES[b].x}%`} y2={`${NODES[b].y}%`}
              stroke="rgba(161,95,228,0.2)"
              strokeWidth="0.3"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.3 + i * 0.1, ease: "easeOut" }}
            />
          ))}

          {CONNECTIONS.map(([a, b], i) => (
            <motion.circle
              key={`pulse-${NODES[a].id}-${NODES[b].id}`}
              r="0.6"
              fill="#A15FE4"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 0.8, 0],
                cx: [`${NODES[a].x}%`, `${NODES[b].x}%`],
                cy: [`${NODES[a].y}%`, `${NODES[b].y}%`],
              }}
              transition={{
                duration: 2.5,
                delay: 1.5 + i * 0.6,
                repeat: Infinity,
                repeatDelay: CONNECTIONS.length * 0.6,
                ease: "easeInOut",
              }}
            />
          ))}
        </svg>

        {NODES.map((node, i) => {
          const Icon = node.icon;
          return (
            <motion.div
              key={node.id}
              className="absolute flex flex-col items-center gap-2"
              style={{ left: `${node.x}%`, top: `${node.y}%`, transform: "translate(-50%, -50%)" }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.2 + i * 0.12 }}
            >
              <motion.div
                className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/30 bg-background/80 shadow-lg shadow-primary/10 backdrop-blur-md"
                whileHover={{ scale: 1.15, borderColor: "rgba(161,95,228,0.6)" }}
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <Icon className="h-5 w-5 text-primary" />
              </motion.div>
              <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">{node.label}</span>
            </motion.div>
          );
        })}

        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/20 bg-primary/5"
          style={{ width: 160, height: 160 }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.08, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/10"
          style={{ width: 260, height: 260 }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.15, 0.04, 0.15] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />
      </div>
    </div>
  );
}
